import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import TemplatePickerModal from './TemplatePickerModal';
import TokenHelperBar from './TokenHelperBar';
import OfficeHoursHint from './OfficeHoursHint';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { communicationsApi } from '../../api/communications';
import toast from '../../utils/toast';

/**
 * TabBroadcast - WhatsApp Broadcast Mode (ToS-Safe)
 * Standalone broadcast composer with template support
 */
const TabBroadcast = () => {
  const [searchParams] = useSearchParams();
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [message, setMessage] = useState('');
  const [recipients, setRecipients] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-load template if templateId in URL
  useEffect(() => {
    const templateId = searchParams.get('templateId');
    if (templateId) {
      loadTemplate(templateId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedTemplate) {
      setMessage(selectedTemplate.content);
    }
  }, [selectedTemplate]);

  const loadTemplate = async (templateId) => {
    try {
      const response = await communicationsApi.templates.get(templateId);
      if (response.template) {
        setSelectedTemplate(response.template);
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setMessage(template.content);
  };

  const handleCopyMessage = () => {
    if (!message.trim()) {
      toast.error('Please compose a message first');
      return;
    }
    const fallback = (text) => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) { /* ignore */ }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(message).catch(() => fallback(message));
    } else {
      fallback(message);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenWhatsApp = () => {
    if (!message.trim()) {
      toast.error('Please compose a message first');
      return;
    }

    const encodedMessage = encodeURIComponent(message);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try WhatsApp app first
      window.location.href = `whatsapp://send?text=${encodedMessage}`;
      
      // Fallback to wa.me after 1 second if app doesn't open
      setTimeout(() => {
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
      }, 1000);
    } else {
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }
  };

  const handleDownloadVCF = async () => {
    if (!recipients.trim()) {
      toast.error('Please add recipient phone numbers first');
      return;
    }

    try {
      setLoading(true);
      
      // Parse recipients
      const phoneNumbers = recipients.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (phoneNumbers.length === 0) {
        toast.error('No valid phone numbers found');
        return;
      }

      // Generate VCF content
      let vcfContent = '';
      phoneNumbers.forEach((phone, index) => {
        vcfContent += `BEGIN:VCARD\n`;
        vcfContent += `VERSION:3.0\n`;
        vcfContent += `FN:Contact ${index + 1}\n`;
        vcfContent += `TEL:${phone}\n`;
        vcfContent += `END:VCARD\n`;
      });

      // Download VCF
      const blob = new Blob([vcfContent], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `broadcast_contacts_${Date.now()}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`VCF file downloaded with ${phoneNumbers.length} contacts!`);
    } catch (err) {
      console.error('VCF generation error:', err);
      toast.error('Could not generate VCF file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const insertToken = (token) => {
    setMessage(prev => prev + token);
  };

  return (
    <div 
      className="p-6 overflow-y-auto"
      style={{
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">WhatsApp Broadcast Mode</h2>
          <Button
            onClick={() => setShowTemplatePicker(true)}
            variant="outline"
            className="bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200"
          >
            📝 Pick Template
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          ToS-compliant user-assisted broadcast helper. Manual steps ensure WhatsApp compliance.
        </p>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">
                100% ToS-Safe Manual Broadcast
              </h3>
              <p className="text-sm text-blue-800">
                This tool helps you compose messages, generate WhatsApp deeplinks, 
                and export contacts. You manually send via WhatsApp Broadcast Lists 
                to stay compliant with WhatsApp Terms of Service.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Office Hours Hint */}
      <OfficeHoursHint 
        suggestedTime={new Date(Date.now() + 3600000).toISOString()}
        onUseSuggestion={() => {
          toast.info('Schedule feature coming soon! Use the suggested time manually.');
        }}
      />

      {/* Selected Template Display */}
      {selectedTemplate && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-lg text-green-900">
              ✓ Template Selected: {selectedTemplate.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-800 mb-2">
              Language: {selectedTemplate.lang.toUpperCase()} | 
              Category: {selectedTemplate.category.replace(/_/g, ' ')}
            </p>
            <div className="bg-white p-3 rounded border border-green-200 text-xs">
              <p className="whitespace-pre-wrap">{selectedTemplate.content}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Composer */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Compose Your Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Message Text</label>
            <TokenHelperBar onInsertToken={insertToken} />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here or pick a template..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-32"
              style={{ minHeight: '128px' }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Characters: {message.length} | Use tokens like {'{{first_name}}'} for personalization
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Recipients (Optional - for VCF export)
            </label>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="+60123456789&#10;+60198765432&#10;+60133334444"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
              style={{ minHeight: '100px' }}
            />
            <p className="text-xs text-gray-500 mt-1">
              One phone number per line with country code (e.g., +60123456789)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-3">
            <Button
              onClick={handleCopyMessage}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              style={{ minHeight: '48px' }}
            >
              {copied ? '✅ Message Copied!' : '📋 Copy Message'}
            </Button>

            <Button
              onClick={handleOpenWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
              style={{ minHeight: '48px' }}
            >
              💬 Open WhatsApp
            </Button>

            <Button
              onClick={handleDownloadVCF}
              disabled={loading}
              className="w-full bg-gray-700 hover:bg-gray-800 text-white font-semibold disabled:opacity-50"
              style={{ minHeight: '48px' }}
            >
              {loading ? '⏳ Generating...' : '📥 Download Contacts (VCF)'}
            </Button>
          </div>

          {/* Helper Text */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">
                📱 How to Send Broadcast
              </h4>
              <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                <li>Copy your message using the button above</li>
                <li>Open WhatsApp and go to "Broadcast Lists"</li>
                <li>Create a new broadcast or select existing list</li>
                <li>Paste your message and send manually</li>
                <li>Import VCF contacts to your phone if needed</li>
              </ol>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
};

export default TabBroadcast;
